import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Stack, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';
import { getInkSticker, getArtworkThumbnail } from '../productionApi';
import { productionKeys } from '../queryKeys';
import { GanttEntry } from './ganttTypes';
import { TOKENS } from './ganttConfig';

interface Props {
  entry: GanttEntry | null;
  open: boolean;
  onClose: () => void;
}

const PREVIEW_WIDTH = 460;
const LMQ_RED = '#c62828';

const kg = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2)} kg`);
const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

/**
 * InkDemandDialog — board-native ink-demand report for a scheduled job. Opened
 * from a Gantt bar's action menu.
 *
 * SAME SOURCE AS THE INK STICKER, deliberately: this reads
 * /production/jobs/{job}/ink-sticker and renders its `separations_in_order`, so
 * the board and the Job Bag sticker can never quote different kg for the same
 * job. Two screens disagreeing about how much ink to order is worse than either
 * being slightly off.
 *
 * It used to fetch /ink-demand at a flat mileage=1 and scale the kg client-side
 * against an editable mileage field (default 1.5). That broke the ink model in
 * two ways:
 *   1. Mileage is per-COLOUR-CLASS, not per job — ECG/process names cost at
 *      1.2 g/m², Pantone/brand spots at 1.5 (helpers/ink_demand.py). A flat
 *      rate over-stated every process separation by 25%: on AAR4728, Black read
 *      3.44 kg here vs 2.75 kg on the sticker, and the job total 8.99 vs 7.98.
 *   2. Scaling linearly is invalid whenever a per-SKU mileage override exists
 *      (production.ink_sku_separation.mileage_g_per_m2). An override WINS over
 *      the requested mileage, so the returned kg isn't proportional to it — the
 *      client multiply would then compound on top of the override.
 * The mileage knob is gone with it: the applied rate is shown per row instead,
 * and calibration belongs in Ink Assign where it persists, not in a throwaway
 * dialog field. Coatings now appear as rows (the sticker costs them at 100%
 * coverage) rather than being listed as "skipped".
 */
export default function InkDemandDialog({ entry, open, onClose }: Props) {
  // The SAME query key the Ink Sticker view uses, so opening one warms the
  // other and they can't drift apart in-session.
  const q = useQuery({
    queryKey: productionKeys.inkSticker(entry?.job ?? ''),
    queryFn: () => getInkSticker(entry!.job),
    enabled: !!entry && open,
    staleTime: 5 * 60 * 1000,
  });

  // Artwork thumbnail (same cached endpoint the board hover uses).
  const thumb = useQuery({
    queryKey: entry ? productionKeys.artworkThumbnail(entry.job, PREVIEW_WIDTH) : ['artwork-thumbnail-disabled'],
    queryFn: () => getArtworkThumbnail(entry!.job, PREVIEW_WIDTH),
    enabled: !!entry && open,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  const r = q.data;
  // Artwork file order — NOT re-sorted heaviest-first as this dialog used to do.
  // It's the order the sticker prints and the order the operator reads down the
  // press, so the two documents line up row for row.
  const rows = r?.separations_in_order ?? [];
  // Ink only, matching the sticker: coatings are a flat 100%-coverage estimate
  // and are listed as rows but deliberately excluded from the ink total.
  const totalKg = r?.total_demand_kg ?? null;

  const artworkProblem =
    r && (r.artwork_status === 'not_found' || r.artwork_status === 'error');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <OpacityRoundedIcon sx={{ color: TOKENS.accent }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Ink demand</Typography>
            {entry && (
              <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
                <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, color: TOKENS.ink2 }}>
                  {entry.job}
                </Box>
                {entry.custName ? ` · ${entry.custName}` : ''} · on {entry.press}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* artwork preview — cached PDF thumbnail; hidden if unavailable */}
        {thumb.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120, mb: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {!thumb.isLoading && thumb.data?.data_url && (
          <Box sx={{
            mb: 2, p: 1, borderRadius: '10px', bgcolor: TOKENS.lineSoft,
            display: 'flex', justifyContent: 'center',
          }}>
            <Box component="img" src={thumb.data.data_url} alt={`Artwork ${entry?.job}`}
              sx={{ display: 'block', maxWidth: '100%', maxHeight: 240, borderRadius: '6px' }} />
          </Box>
        )}

        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 5 }} spacing={1.5}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>
              Reading artwork coverage from Cloudflow…
            </Typography>
          </Stack>
        )}

        {q.isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Couldn’t load ink demand: {(q.error as Error)?.message ?? 'unknown error'}
          </Alert>
        )}

        {r && (
          <>
            {artworkProblem && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Artwork not found in Cloudflow — check the item code / Pre-Press file
                naming. {r.cloudflow_error ? `(${r.cloudflow_error})` : ''}
              </Alert>
            )}
            {/* The wrong-artwork-version signal (Prism says ECG but the file
                still carries Pantone spots) — same check the sticker renders,
                and the loudest thing on either screen when it fires. */}
            {r.ink_spec_mismatch && r.ink_spec_mismatch_detail && (
              <Alert severity="error" sx={{ mb: 2 }}>{r.ink_spec_mismatch_detail}</Alert>
            )}
            {!artworkProblem && r.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {r.warnings.filter((w) => w !== r.ink_spec_mismatch_detail).join(' ')}
              </Alert>
            )}

            {/* inputs the number is built from — transparent, and measured
                rather than typed: mileage is resolved per colour class by the
                backend (ECG/process 1.2, Pantone 1.5) and per-SKU overrides
                from Ink Assign win over both, so there is no single job-level
                figure to show here. The applied rate is on each row instead. */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2,
              p: 1.5, borderRadius: '10px', bgcolor: TOKENS.lineSoft,
            }}>
              <Metric label="Run length" value={
                r.run_length_sheets != null
                  ? `${r.run_length_sheets.toLocaleString()} sheets` : '—'} />
              <Metric label="Sheet size" value={
                r.sheet_width_mm && r.sheet_height_mm
                  ? `${r.sheet_width_mm} × ${r.sheet_height_mm} mm` : '—'} />
              <Metric label="Sheet area" value={
                r.sheet_area_m2 != null ? `${r.sheet_area_m2.toFixed(3)} m²` : '—'} />
              <Metric label="Material" value={r.material_desc ?? '—'} mono={false} />
            </Box>

            {rows.length > 0 ? (
              <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Separation</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Coverage</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Demand</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={`${row.name}-${i}`}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{
                            width: 14, height: 14, borderRadius: '3px', flexShrink: 0,
                            bgcolor: row.hex_colour ?? '#ccc',
                            border: '1px solid rgba(0,0,0,0.15)',
                          }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{
                              fontSize: 13, fontWeight: 600, lineHeight: 1.2,
                              color: row.low_migration ? LMQ_RED : TOKENS.ink,
                            }}>
                              {/* The ALLOCATED ink's own colour when one is
                                  assigned — what the operator actually loads —
                                  falling back to the separation name. Same rule
                                  as the sticker. */}
                              {row.ink_colour || row.name}
                              {row.low_migration && (
                                <Box component="span" sx={{ ml: 0.5, fontSize: 9.5, fontWeight: 800, color: LMQ_RED }}>
                                  LMQ
                                </Box>
                              )}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: TOKENS.ink3 }}>
                              {row.series ? `${row.series} · ` : ''}
                              {row.kind?.replace('_', ' ')}
                              {/* The rate this row costed at — the thing the
                                  removed mileage box used to (wrongly) control. */}
                              {row.mileage_g_per_m2 != null && ` · ${row.mileage_g_per_m2} g/m²`}
                              {row.mileage_overridden && ' (override)'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {pct(row.coverage_frac)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      }}>
                        {kg(row.demand_kg)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total ink</TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{
                      fontWeight: 800, fontSize: 14, color: TOKENS.accent,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {kg(totalKg)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              !q.isLoading && (
                <Typography sx={{ fontSize: 13, color: TOKENS.ink3, py: 2, textAlign: 'center' }}>
                  No ink separations to report.
                </Typography>
              )
            )}

            {/* Non-printing layers (die lines, dimension marks, registration)
                are dropped by classify_separations before demand is computed —
                the sticker doesn't list them either, so neither does this. */}
            <Typography sx={{ fontSize: 10.5, color: TOKENS.ink3, mt: 1.5, textAlign: 'center' }}>
              Same figures as the Job Bag ink sticker. Coatings are estimated at
              100% coverage and excluded from the ink total.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Metric({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, color: TOKENS.ink3 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 13, fontWeight: 600, color: TOKENS.ink,
        fontFamily: mono ? TOKENS.monoFont : 'inherit',
      }}>
        {value}
      </Typography>
    </Box>
  );
}
